/* eslint-disable max-len */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

// Icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faTiktok, faLinkedin } from '@fortawesome/free-brands-svg-icons';

// Logo
import logo from '../../assets/images/logo/ZovLogoWhite.png';

import './footer.scss';

// Animation variants
const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const columnVariants = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
};

const linkVariants = {
  initial: { opacity: 0, x: -10 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
};

const socialVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 20,
    },
  },
  hover: {
    y: -6,
    scale: 1.1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 15,
    },
  },
  tap: {
    scale: 0.95,
  },
};

function Footer() {
  const { t } = useTranslation();
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  return (
    <footer className="footer">
      {/* Main Footer Content */}
      <Box className="main-footer">
        <Container maxWidth="lg">
          <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-100px' }}
          >
            <Grid container spacing={6}>
              {/* Logo and Social Media Column */}
              <Grid item xs={12} md={4} className="footer-column">
                <motion.div variants={columnVariants}>
                  <Box className="footer-logo-section">
                    <motion.img
                      src={logo}
                      alt="ZOV Furniture LLC"
                      className="footer-logo"
                      whileHover={{ filter: 'drop-shadow(0 0 20px rgba(201, 169, 98, 0.5))' }}
                      transition={{ duration: 0.3 }}
                    />
                    <Typography variant="body2" className="footer-tagline">
                      {t('footer.tagline')}
                    </Typography>
                    <Box className="social-media-container">
                      <motion.a
                        href="https://www.instagram.com/zovfurnitureuae/?igsh=NGFrdXd1NTdyNm1i"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        variants={socialVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <FontAwesomeIcon icon={faInstagram} />
                      </motion.a>
                      <motion.a
                        href="https://www.tiktok.com/@zovfurnitureuae"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        variants={socialVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <FontAwesomeIcon icon={faTiktok} />
                      </motion.a>
                      <motion.a
                        href="https://www.linkedin.com/company/zov-furniture-l-l-c/about/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon"
                        variants={socialVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <FontAwesomeIcon icon={faLinkedin} />
                      </motion.a>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>

              {/* Quick Links Column */}
              <Grid item xs={12} md={4} className="footer-column">
                <motion.div variants={columnVariants}>
                  <Typography variant="h6" className="footer-heading">
                    {t('footer.quickLinks')}
                  </Typography>
                  <motion.ul
                    className="footer-links"
                    variants={containerVariants}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                  >
                    {[
                      { to: '/about', label: t('footer.aboutUs') },
                      { to: '/concepts', label: t('footer.ourCollections') },
                      { to: '/portfolio', label: t('footer.portfolio') },
                      { to: '/location', label: t('footer.showroom') },
                      { to: '/contact', label: t('footer.contact') },
                      { to: '/privacy-policy', label: t('footer.privacyPolicy') },
                    ].map((link) => (
                      <motion.li key={link.to} variants={linkVariants}>
                        <Link to={link.to} className="footer-link-animated">{link.label}</Link>
                      </motion.li>
                    ))}
                  </motion.ul>
                </motion.div>
              </Grid>

              {/* Contact Information Column */}
              <Grid item xs={12} md={4} className="footer-column contact-column">
                <motion.div variants={columnVariants}>
                  <Typography variant="h6" className="footer-heading">
                    {t('footer.contactUs')}
                  </Typography>
                  <Box className="contact-info">
                    <motion.div
                      className="contact-item"
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <LocationOnIcon className="contact-icon" />
                      <Typography variant="body2" sx={{ textAlign: 'left' }}>
                        {t('footer.address')}
                      </Typography>
                    </motion.div>

                    <motion.div
                      className="contact-item"
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <AccessTimeIcon className="contact-icon" />
                      <Typography variant="body2">
                        {t('footer.openEveryday')}
                      </Typography>
                    </motion.div>

                    <motion.div
                      className="contact-item"
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <EmailIcon className="contact-icon" />
                      <a href="mailto:info@zov.ae" className="contact-link">info@zov.ae</a>
                    </motion.div>

                    <motion.div
                      className="contact-item"
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <PhoneIcon className="contact-icon" sx={{ mt: '4px' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <a href="tel:+971509636346" className="contact-link">+971 50 963 6346</a>
                        <a href="tel:+971522518853" className="contact-link">+971 52 251 8853</a>
                      </Box>
                    </motion.div>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Google Map */}
      <Box className="map-container">
        <iframe
          title="Location of ZOV Furniture LLC"
          className="google-map"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=24.480538197575605, 54.357646964641795`}
        />
      </Box>

      {/* Copyright Footer */}
      <Box className="bottom-footer">
        <Container maxWidth="lg">
          <Box className="bottom-footer-content">
            <Typography variant="body2" className="copyright">
              © {new Date().getFullYear()} {t('footer.copyright')}
            </Typography>
            <Typography variant="body2" className="branding">
              {t('footer.madeBy')} <a href="https://brxdigital.com" target="_blank" rel="noopener noreferrer">brxdigital.com</a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* WhatsApp Floating Button */}
      <motion.a
        href="https://wa.me/971522518853?text=Hello, I need information about ZOV luxury furniture collections."
        className="whatsapp-button"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contact us on WhatsApp"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
          delay: 1,
        }}
        whileHover={{
          scale: 1.15,
          y: -5,
          boxShadow: '0 15px 35px rgba(37, 211, 102, 0.5)',
        }}
        whileTap={{ scale: 0.95 }}
      >
        <WhatsAppIcon />
      </motion.a>
    </footer>
  );
}

export default Footer;
